import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingTagType,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import type { AIUsageStats } from "@repo/shared/types";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { Bold, Heading, Italic, List, ListOrdered, Redo, Sparkles, Undo } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  appLanguageToTranscriptionCode,
  VoiceInputButton,
} from "@/components/ui/voice-input-button";
import { post } from "@/lib/api/_client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./markdown-editor-with-toolbar.i18n";

/**
 * `levels` — the block select offers Paragraph / H1 / H2 / H3 (general copy).
 * `single` — no block select; a Heading toggle button beside Bold/Italic flips
 * the current block between the one heading and a paragraph: flow copy has
 * exactly one heading style (DESIGN.md › Flow component headers), so the editor
 * offers exactly one. QL-296.
 */
export type MarkdownEditorHeadings = "levels" | "single";

/** The heading level `single` mode creates — the level FlowComponentHeader renders. */
export const SINGLE_HEADING_TAG: HeadingTagType = "h3";

type ToolbarPluginProps = {
  showSpeechToText?: boolean;
  showAI?: boolean;
  headings?: MarkdownEditorHeadings;
  onAIStatsUpdate?: (stats: AIUsageStats | null) => void;
  aiContext?: {
    flowName: string;
    clientName: string;
    pageName: string;
    riskScore?: number;
    language?: string;
  };
};

export function ToolbarPlugin({
  showSpeechToText = false,
  showAI = true,
  headings = "levels",
  onAIStatsUpdate,
  aiContext,
}: ToolbarPluginProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState("paragraph");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isImproving, setIsImproving] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text formatting
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));

      // Update block type
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isHeadingNode(element)) {
          const tag = element.getTag();
          setBlockType(tag);
        } else if ($isQuoteNode(element)) {
          setBlockType("quote");
        } else {
          setBlockType("paragraph");
        }
      }
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, updateToolbar]);

  // Register undo/redo state listeners
  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const _formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  };

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const formatNumberedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  // Insert a transcript from the voice-input button at the cursor
  // (leading space; append to the end if there is no selection).
  const insertTranscript = useCallback(
    (text: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(` ${text}`);
        } else {
          const root = $getRoot();
          root.selectEnd();
          const newSelection = $getSelection();
          if ($isRangeSelection(newSelection)) {
            newSelection.insertText(` ${text}`);
          }
        }
      });
    },
    [editor],
  );

  const improveText = async (promptType: string) => {
    if (isImproving) return;

    // Build context string - used only for AI understanding, not to include in output
    let contextStr = "";
    if (aiContext) {
      contextStr = `\n\n[For context only - DO NOT include this in your response]\nThis note is about: ${aiContext.clientName}\nFlow type: ${aiContext.flowName}\nCurrent section: ${aiContext.pageName}`;
      if (aiContext.riskScore !== undefined) {
        contextStr += `\nClient's risk score: ${aiContext.riskScore}`;
      }
      contextStr += `\n[End of context - respond with improved text only]`;
    }

    const prompts: Record<string, string> = {
      improve: `Improve the writing quality, clarity, and professionalism of this text while maintaining its core message and any specific financial details.${contextStr}`,
      simplify: `Simplify this text to make it clearer and easier to understand for clients, while preserving all important financial information.${contextStr}`,
      expand: `Expand this text with more detail and context appropriate for a financial advisor's client notes, maintaining a professional tone.${contextStr}`,
      formal: `Rewrite this text in a more formal and professional tone suitable for official financial advisory communications.${contextStr}`,
      concise: `Make this text more concise and to-the-point while retaining all critical financial information and recommendations.${contextStr}`,
    };

    const prompt = prompts[promptType];
    if (!prompt) return;

    setIsImproving(true);

    try {
      // Get all text content from the editor
      const allText = editor.getEditorState().read(() => {
        const root = $getRoot();
        return root.getTextContent();
      });

      if (!allText.trim()) {
        alert(t.pleaseEnterTextFirst);
        setIsImproving(false);
        return;
      }

      // Call the API
      const result = await post<{ improvedText: string; stats: AIUsageStats }>(
        "/internal/flows/improve-text",
        { text: allText, prompt, language: aiContext?.language },
      );
      const improvedText = result.improvedText;

      if (!improvedText) {
        throw new Error("No improved text returned");
      }

      // Replace all content with improved text
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        root.selectEnd();

        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(improvedText);
        }
      });

      // Update AI stats in the footer
      if (onAIStatsUpdate && result.stats) {
        onAIStatsUpdate(result.stats);
      }
    } catch (_error) {
      alert(t.failedToImproveText);
    } finally {
      setIsImproving(false);
    }
  };

  const singleHeading = headings === "single";
  const isHeadingBlock = /^h[1-6]$/.test(blockType);

  // Single-heading mode: the toggle flips the block between the one heading and a
  // paragraph. A legacy H1/H2 in stored markdown counts as "on" too — there is one
  // heading, whatever level produced it — and toggling it off and on re-creates
  // it at SINGLE_HEADING_TAG.
  const toggleHeading = () => {
    if (isHeadingBlock) formatParagraph();
    else formatHeading(SINGLE_HEADING_TAG);
  };

  const getBlockTypeLabel = () => {
    switch (blockType) {
      case "h1":
        return t.heading1;
      case "h2":
        return t.heading2;
      case "h3":
        return t.heading3;
      case "h4":
        return "Heading 4";
      case "h5":
        return "Heading 5";
      case "h6":
        return "Heading 6";
      case "quote":
        return t.quote;
      case "bullet":
        return t.bulletList;
      case "number":
        return t.numberedList;
      default:
        return t.paragraph;
    }
  };

  return (
    // Design 14k — one 32px row that scrolls sideways rather than wrapping; a
    // toolbar that grows a second line shifts every field under it.
    <div className="flex h-8 shrink-0 items-center gap-0.5 overflow-x-auto border-b bg-muted px-1">
      {!singleHeading && (
        <>
          <Select
            value={
              blockType === "quote" ||
              blockType === "h4" ||
              blockType === "h5" ||
              blockType === "h6"
                ? "paragraph"
                : blockType
            }
            onValueChange={(value) => {
              if (value === "paragraph") formatParagraph();
              else if (value === "h1") formatHeading("h1");
              else if (value === "h2") formatHeading("h2");
              else if (value === "h3") formatHeading("h3");
            }}
          >
            <SelectTrigger className="h-6 w-[128px] shrink-0 text-xs">
              <SelectValue>{getBlockTypeLabel()}</SelectValue>
            </SelectTrigger>
            <SelectContent className="z-[100]">
              <SelectItem value="paragraph">{t.paragraph}</SelectItem>
              <SelectItem value="h1">{t.heading1}</SelectItem>
              <SelectItem value="h2">{t.heading2}</SelectItem>
              <SelectItem value="h3">{t.heading3}</SelectItem>
            </SelectContent>
          </Select>

          <div className="h-4 w-px shrink-0 bg-border" />
        </>
      )}

      <Button
        variant="ghost"
        size="icon"
        tooltip={t.undo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        disabled={!canUndo}
        className="h-6 w-6 shrink-0 rounded-sm p-0"
        type="button"
      >
        <Undo className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        tooltip={t.redo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        disabled={!canRedo}
        className="h-6 w-6 shrink-0 rounded-sm p-0"
        type="button"
      >
        <Redo className="h-3.5 w-3.5" />
      </Button>

      <div className="h-4 w-px shrink-0 bg-border" />

      {singleHeading && (
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleHeading}
          aria-pressed={isHeadingBlock}
          className={cn(
            "h-6 w-6 shrink-0 rounded-sm p-0",
            isHeadingBlock && "bg-card text-foreground shadow-sm",
          )}
          type="button"
          tooltip={t.heading}
        >
          <Heading className="h-3.5 w-3.5" />
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={formatBold}
        aria-pressed={isBold}
        className={cn(
          "h-6 w-6 shrink-0 rounded-sm p-0",
          isBold && "bg-card text-foreground shadow-sm",
        )}
        type="button"
        tooltip={t.bold}
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={formatItalic}
        aria-pressed={isItalic}
        className={cn(
          "h-6 w-6 shrink-0 rounded-sm p-0",
          isItalic && "bg-card text-foreground shadow-sm",
        )}
        type="button"
        tooltip={t.italic}
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>

      <div className="h-4 w-px shrink-0 bg-border" />

      <Button
        variant="ghost"
        size="icon"
        onClick={formatBulletList}
        className="h-6 w-6 shrink-0 rounded-sm p-0"
        type="button"
        tooltip={t.bulletList}
      >
        <List className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={formatNumberedList}
        className="h-6 w-6 shrink-0 rounded-sm p-0"
        type="button"
        tooltip={t.numberedList}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </Button>

      {showSpeechToText && (
        <>
          <div className="h-4 w-px shrink-0 bg-border" />

          {/* Voice-to-text: record → Mistral Voxtral. */}
          <VoiceInputButton
            onTranscript={insertTranscript}
            defaultLanguage={appLanguageToTranscriptionCode(aiContext?.language || userLanguage)}
            labels={{
              start: t.startVoiceRecording,
              stop: t.stopVoiceRecording,
              transcribing: t.transcribing,
              error: t.voiceError,
              micDenied: t.micDenied,
              language: t.transcriptionLanguage,
            }}
          />
        </>
      )}

      {showAI && (
        <>
          <div className="h-4 w-px shrink-0 bg-border" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 px-2 shrink-0 gap-1",
                  isImproving && "opacity-50 cursor-not-allowed",
                )}
                type="button"
                disabled={isImproving}
                tooltip={t.aiTextImprovement}
              >
                <span className="text-xs flex items-center gap-0.5">
                  AI
                  {isImproving && (
                    <>
                      <span className="animate-bounce" style={{ animationDelay: "0ms" }}>
                        .
                      </span>
                      <span className="animate-bounce" style={{ animationDelay: "150ms" }}>
                        .
                      </span>
                      <span className="animate-bounce" style={{ animationDelay: "300ms" }}>
                        .
                      </span>
                    </>
                  )}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => improveText("improve")} disabled={isImproving}>
                <Sparkles className="h-4 w-4 mr-2" />
                {t.improveWriting}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => improveText("simplify")} disabled={isImproving}>
                {t.simplify}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => improveText("expand")} disabled={isImproving}>
                {t.expandDetail}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => improveText("formal")} disabled={isImproving}>
                {t.makeFormal}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => improveText("concise")} disabled={isImproving}>
                {t.makeConcise}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}
